import * as THREE from "three";
import { MATS } from "./materials.js";
import { CAR, CAR_TIERS, HERO_LOT,
  BOUNDS, LOTS, Z, kerbZ, walkNearZ, walkFarZ, roadCentreZ, roadCurve,
} from "./spec.js";

/**
 * Everything from the house frontage out to the far side of the road: the
 * curving carriageway, kerb, sidewalk, driveways with their flared aprons,
 * front walkways, and the street furniture (lamps, storm
 * drains, and the colliders under the parked car).
 *
 * The crescent bends with roadCurve(x), so nothing here is one long box.
 * Flat surfaces are ribbons -- strips of quads sampled every ~1.5 m whose
 * edges sit exactly on the curve functions, so there are no visible steps.
 * The kerb is short boxes rotated to the local tangent, with axis-aligned
 * colliders (the collision system only does AABBs, and at 0.13 m the error
 * from rotation is far below anything the cat can feel).
 */

// Surface stack, bottom to top, so nothing z-fights: lawn 0, verge 0.013
// (ground.js), then ours.
const Y = {
  road: 0.012,
  paint: 0.019,     // road markings, just above the tarmac
  drive: 0.015,
  walk: 0.02,
  apron: 0.024,     // over the verge slab where the driveway crosses it
  path: 0.017,
  joint: 0.027,     // expansion joints, just above the walk
};

const KERB_H = 0.13;      // a real kerb, and well inside the cat's 0.23 m
                          // step so it is mounted at a walk.  This used to
                          // read "== MOVE.stepHeight"; the spec's step height
                          // was corrected to 0.23 and the kerb did not move.
const KERB_D = 0.30;
const APRON_HALF = 2.55;  // half-width of the dropped-kerb opening per drive

/** d(roadCurve)/dx, numerically, so it tracks spec.js if the curve changes. */
function slope(x) {
  return (roadCurve(x + 0.5) - roadCurve(x - 0.5));
}

/**
 * A flat, up-facing strip of quads between two z(x) curves.  One merged
 * geometry, top face only -- these always sit a few mm over another surface,
 * so sides and underside would never be seen.
 */
function ribbon(b, x0, x1, step, y, zNear, zFar, mat, opts = {}) {
  const n = Math.max(1, Math.ceil((x1 - x0) / step));
  const pos = [];
  for (let i = 0; i < n; i++) {
    const xa = x0 + ((x1 - x0) * i) / n;
    const xb = x0 + ((x1 - x0) * (i + 1)) / n;
    // A,B on the near edge, C,D on the far; wound CCW seen from above
    pos.push(
      xa, y, zNear(xa),  xa, y, zFar(xa),  xb, y, zFar(xb),
      xa, y, zNear(xa),  xb, y, zFar(xb),  xb, y, zNear(xb),
    );
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  const nrm = new Float32Array(pos.length);
  for (let i = 1; i < nrm.length; i += 3) nrm[i] = 1;
  g.setAttribute("normal", new THREE.BufferAttribute(nrm, 3));
  b.add(g, mat, { collider: null, ao: 0, tile: opts.tile ?? 1.2, ...opts });
}

/** One flat quad from four [x, z] corners: near-left, near-right, far-right, far-left. */
function flatQuad(b, [a, bb, c, d], y, mat, opts = {}) {
  const pos = [
    a[0], y, a[1],  d[0], y, d[1],  c[0], y, c[1],
    a[0], y, a[1],  c[0], y, c[1],  bb[0], y, bb[1],
  ];
  const g = new THREE.BufferGeometry();
  g.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  const nrm = new Float32Array(pos.length);
  for (let i = 1; i < nrm.length; i += 3) nrm[i] = 1;
  g.setAttribute("normal", new THREE.BufferAttribute(nrm, 3));
  b.add(g, mat, { collider: null, ao: 0, tile: opts.tile ?? 1.2, ...opts });
}

// ---------------------------------------------------------------------------
// The carriageway

function carriageway(b) {
  // BOUNDS.maxZ is sized for roadFar plus the crescent's full bow, so the
  // far edge follows the curve all the way out with no clamp.
  ribbon(b, BOUNDS.minX, BOUNDS.maxX, 1.5, Y.road,
         (x) => kerbZ(x) + 0.10, (x) => Z.roadFar + roadCurve(x),
         MATS.roadTop, { tile: 3.0 });

  // Dashed centre line, bending with the crescent.
  for (let x = -29.5; x <= 29.5; x += 6.5) {
    ribbon(b, x - 1.1, x + 1.1, 1.1, Y.paint,
           (xx) => roadCentreZ(xx) - 0.06, (xx) => roadCentreZ(xx) + 0.06,
           MATS.lineYellow, { tile: 1 });
  }

  // A couple of manhole covers along the middle of the lane.
  for (const x of [-16, 5.5]) {
    b.post(x, Y.road, roadCentreZ(x) + 1.3, 0.45, 0.022, MATS.metalDark,
           { solid: false, segments: 12, tile: 0 });
  }
}

// ---------------------------------------------------------------------------
// Kerb: rotated boxes along kerbZ, skipping the driveway openings

function kerbRun(b) {
  // Open spans where driveways drop the kerb.
  const cuts = LOTS.map((l) => [l.house.garageX - APRON_HALF,
                                l.house.garageX + APRON_HALF]);
  const spans = [];
  let start = BOUNDS.minX;
  for (const [lo, hi] of cuts) { spans.push([start, lo]); start = hi; }
  spans.push([start, BOUNDS.maxX]);

  // Segment length adapts to the tangent: by the map corners the crescent
  // runs ~35 deg to the x axis, where fixed 1.5 m boxes would leave wedge
  // gaps and put the AABB collider well off the kerb line.  Capping the
  // z-run per segment keeps neighbours butted and each collider on its box.
  const MAX_Z_RUN = 0.24;
  for (const [lo, hi] of spans) {
    let a = lo;
    while (a < hi - 0.02) {
      const S = Math.min(1.5,
        Math.max(0.3, MAX_Z_RUN / Math.max(Math.abs(slope(a)), 1e-6)));
      const end = Math.min(a + S, hi);
      const len = end - a;
      const xc = (a + end) / 2;
      const th = Math.atan(slope(xc));
      // secant-scaled so the box still spans [a, end] in x once rotated
      const g = new THREE.BoxGeometry(len / Math.cos(th) + 0.06, KERB_H, KERB_D);
      g.rotateY(-th);                         // follow the local tangent
      g.translate(xc, KERB_H / 2, kerbZ(xc));
      b.add(g, MATS.concrete, {
        tile: 1.4,
        // AABB collider; exactly step height, so it is walkable by design
        collider: { x: xc, y: 0, z: kerbZ(xc), w: len + 0.06, h: KERB_H,
                    d: KERB_D + 0.06 + len * Math.abs(slope(xc)) },
      });
      a = end;
    }
  }
}

// ---------------------------------------------------------------------------
// Sidewalk with expansion joints

function sidewalk(b) {
  ribbon(b, BOUNDS.minX, BOUNDS.maxX, 1.5, Y.walk,
         walkNearZ, walkFarZ, MATS.concrete, { tile: 1.5 });
  // Joint lines every 1.5 m, cut square to the walk's local direction: the
  // crescent runs ~35 deg to the x axis by the map corners, far too skewed
  // for z-parallel cuts to pass as expansion joints.
  for (let x = BOUNDS.minX + 1.5; x < BOUNDS.maxX - 0.5; x += 1.5) {
    const th = Math.atan(slope(x));
    const c = Math.cos(th), s = Math.sin(th);
    const zc = (walkNearZ(x) + walkFarZ(x)) / 2;
    const h = (c * (Z.walkFar - Z.walkNear)) / 2 - 0.04;  // half-length of cut
    flatQuad(b, [
      [x + h * s - 0.025 * c, zc - h * c - 0.025 * s],
      [x + h * s + 0.025 * c, zc - h * c + 0.025 * s],
      [x - h * s + 0.025 * c, zc + h * c + 0.025 * s],
      [x - h * s - 0.025 * c, zc + h * c - 0.025 * s],
    ], Y.joint, MATS.concreteDark, { tile: 0.6 });
  }
}

// ---------------------------------------------------------------------------
// Driveways and front walkways

function driveway(b, i) {
  const gx = LOTS[i].house.garageX;
  const mat = i === 1 ? MATS.concrete : MATS.asphalt;   // the street mixes both
  // Lot 1's drive widened toward the house when the grass strip and planting
  // bed came out: its inner edge runs at x = 1.0, dead against the porch edge
  // at x = 0.95 (hero/frontyard.js lays its entry apron on top, at y 0.028+,
  // so the drive surface stays below it).  The kerb cut, apron and dropped
  // kerb keep their width -- only the slab between house and sidewalk grew.
  const inX = i === 1 ? 1.0 : gx - 1.7;

  // Garage to sidewalk.  Short segments: across lot 2's 3.4 m width the curve
  // already shifts the walk edge by ~1.1 m, so even this short edge must bend.
  ribbon(b, inX, gx + 1.7, 1.7, Y.drive,
         () => Z.houseFront - 0.15, (x) => walkNearZ(x) + 0.06, mat, { tile: 2.2 });

  // Flared apron across the verge, walk edge out to the dropped kerb.
  flatQuad(b, [
    [gx - 1.7, walkFarZ(gx - 1.7) - 0.06],
    [gx + 1.7, walkFarZ(gx + 1.7) - 0.06],
    [gx + APRON_HALF, kerbZ(gx + APRON_HALF) + 0.18],
    [gx - APRON_HALF, kerbZ(gx - APRON_HALF) + 0.18],
  ], Y.apron, mat, { tile: 2.2 });

  // Dropped kerb: a run of low blocks so it tracks the curve (the kerb line
  // falls ~1.6 m across lot 2's opening).  5 cm high -- a walk-up.
  for (const off of [-1.7, -0.85, 0, 0.85, 1.7]) {
    b.block(gx + off, 0, kerbZ(gx + off), 1.74, 0.05, 0.42, mat, { tile: 2.2 });
  }

  // The pale concrete drive reads better with a couple of joints.
  if (i === 1) {
    for (const zj of [5.6, 10.2]) {
      flatQuad(b, [
        [inX + 0.1, zj - 0.03], [gx + 1.6, zj - 0.03],
        [gx + 1.6, zj + 0.03], [inX + 0.1, zj + 0.03],
      ], Y.drive + 0.004, MATS.concreteDark, { tile: 0.6 });
    }
  }
}

function frontPath(b, i) {
  const dx = LOTS[i].house.doorX;
  // Starts past the porch footprint so it does not fight the porch slab.
  ribbon(b, dx - 0.55, dx + 0.55, 1.1, Y.path,
         () => Z.houseFront + 2.4, (x) => walkNearZ(x) + 0.06,
         MATS.paver, { tile: 1.1 });
}

// ---------------------------------------------------------------------------
// Street furniture

/**
 * A street lamp on the verge: tapered column, a curved arm reaching over the
 * kerb, and a shallow head.  Only the column is solid -- the arm and head are
 * three metres up, where a collider would just be an invisible snag.
 */
function lamp(b, x) {
  const z = (walkFarZ(x) + kerbZ(x)) / 2;      // centred on the grass verge
  b.post(x, 0, z, 0.14, 0.22, MATS.metalDark, { segments: 8 });          // base
  b.post(x, 0.22, z, 0.10, 4.30, MATS.metalDark,
         { topRadius: 0.07, segments: 8 });                              // column
  // Arm out over the road, drawn as three short steps rather than one bar so
  // it reads as a curve from the pavement.
  const reach = [[0.16, 4.46], [0.52, 4.60], [0.90, 4.66]];
  for (const [dz, y] of reach) {
    b.bar(x, y, z + dz, 0.055, 0.42, "z", MATS.metalDark,
          { segments: 6, solid: false });
  }
  b.block(x, 4.50, z + 1.06, 0.42, 0.10, 0.60, MATS.metalDark, { solid: false });
  b.block(x, 4.44, z + 1.06, 0.34, 0.06, 0.50, MATS.lineWhite, { solid: false });
  b.mark(`lamp${x < 0 ? "W" : "E"}`, x, 0, z);
}

function grate(b, x) {
  const z = kerbZ(x) + 0.42;             // in the gutter, against the kerb
  b.slab(x, 0.016, z, 1.0, 0.55, MATS.metalDark, { thickness: 0.018, tile: 0.8 });
  for (let i = -1; i <= 1; i++) {
    b.block(x + i * 0.28, 0.034, z, 0.07, 0.012, 0.46, MATS.metalPale,
            { solid: false, tile: 0.5 });
  }
  // Inlet plate on the kerb face.
  b.block(x, 0.02, kerbZ(x) + 0.17, 0.95, 0.10, 0.035, MATS.metalDark, { solid: false });
}

// ---------------------------------------------------------------------------
// The parked car

/**
 * What the cat touches where the car is parked.
 *
 * The car's shape is CAR_TIERS in spec.js -- a staircase of tapering slabs
 * the cat can walk end to end, with the full story of where the numbers came
 * from.  This function stands one collider on every tier; the client's
 * car.js draws one box on every tier from the SAME array, which is what
 * keeps the car the cat touches and the car the player sees one object.
 *
 * The colliders go through this Builder because collision is the level's;
 * the boxes do not, because drawing is the client's.  Tier `part` tags are
 * the renderer's business and are ignored here: glass collides like metal.
 *
 * `z` is the car's centre and the nose faces -z, toward the garage; the
 * tier offsets are metres from the centre, already in the street's axes.
 */
function carColliders(b, x, z) {
  for (const { z0, z1, top, w } of CAR_TIERS) {
    b.collider(x, 0, z + (z0 + z1) / 2, w, top, z1 - z0);
  }

  b.mark("carBonnet", x, 0.92, z - 1.5);
  b.mark("carRoof", x, 1.44, z + 0.4);
}

// ---------------------------------------------------------------------------

export function build(b) {
  carriageway(b);
  kerbRun(b);
  sidewalk(b);

  for (let i = 0; i < LOTS.length; i++) {
    driveway(b, i);
    if (i !== HERO_LOT) frontPath(b, i);   // hero/ lays the middle walkway

  }

  // Positions picked to stay clear of the driveway aprons
  // (garageX +/- 2.55: -13.4..-8.3, 1.1..6.2, 15.6..20.7) and the walkways.
  lamp(b, -24.5);
  lamp(b, 8.0);
  grate(b, -6.0);
  grate(b, 10.0);

  carColliders(b, CAR.x, CAR.z);
}
